/**
 * Versioned HTTP contract — `v1` — for JARVIS KNOWLEDGE through the secure API gateway
 * (`services/idea-engine-api`), backing the operator's owner-scoped text/markdown knowledge base
 * (B5 / IDEA-80 — ADR 0090).
 *
 * Same rationale as the other `./http/*` contracts (ADR 0001/0005/0023 §5): the api service and
 * the web app parse the SAME shapes without importing one another. TEXT-FIRST (ADR 0090 §1): the
 * upload body carries the document text the browser read with `file.text()` — no binary, no new
 * dependency; the body is size-capped at the route. Owner is NEVER a body field — every route
 * authorises on the request's derived owner (A6b). Breaking changes ship as `v2`, never by mutation.
 */
import { z } from 'zod';
import { apiEnvelope } from './v1.js';
import { KnowledgeDocumentSchema, KNOWLEDGE_TEXT_MAX_CHARS, KNOWLEDGE_TITLE_MAX_CHARS } from '../knowledge.js';

/**
 * Request body for `POST /v1/knowledge/upload`: the operator-supplied title + the document text
 * (read client-side from a `.txt`/`.md` file). `text` is capped at {@link KNOWLEDGE_TEXT_MAX_CHARS}
 * chars here and the whole body is byte-capped at the route. NO `ownerId` — the derived owner owns
 * the document (A6b).
 */
export const KnowledgeUploadRequestSchema = z.object({
  title: z.string().min(1).max(KNOWLEDGE_TITLE_MAX_CHARS),
  text: z.string().min(1).max(KNOWLEDGE_TEXT_MAX_CHARS),
});
export type KnowledgeUploadRequest = z.infer<typeof KnowledgeUploadRequestSchema>;

/** Success payload for `POST /v1/knowledge/upload`: the ingested document header (status ready|error). */
export const KnowledgeUploadDataSchema = z.object({ document: KnowledgeDocumentSchema });
export type KnowledgeUploadData = z.infer<typeof KnowledgeUploadDataSchema>;

/** Full `POST /v1/knowledge/upload` response envelope. */
export const KnowledgeUploadResponseSchema = apiEnvelope(KnowledgeUploadDataSchema);
export type KnowledgeUploadResponse = z.infer<typeof KnowledgeUploadResponseSchema>;

/** Success payload for `GET /v1/knowledge/documents`: the owner's documents, newest-first. */
export const KnowledgeDocumentsDataSchema = z.object({ documents: z.array(KnowledgeDocumentSchema) });
export type KnowledgeDocumentsData = z.infer<typeof KnowledgeDocumentsDataSchema>;

/** Full `GET /v1/knowledge/documents` response envelope. */
export const KnowledgeDocumentsResponseSchema = apiEnvelope(KnowledgeDocumentsDataSchema);
export type KnowledgeDocumentsResponse = z.infer<typeof KnowledgeDocumentsResponseSchema>;

/** Success payload for `DELETE /v1/knowledge/documents/:id`: the deleted document id. */
export const KnowledgeDeleteDataSchema = z.object({ documentId: z.string().min(1).max(64) });
export type KnowledgeDeleteData = z.infer<typeof KnowledgeDeleteDataSchema>;

/** Full `DELETE /v1/knowledge/documents/:id` response envelope. */
export const KnowledgeDeleteResponseSchema = apiEnvelope(KnowledgeDeleteDataSchema);
export type KnowledgeDeleteResponse = z.infer<typeof KnowledgeDeleteResponseSchema>;
